import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { supabase } from '../lib/supabase';
import { 
  BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer,
  LineChart, Line, PieChart, Pie, Cell, Legend
} from 'recharts';
import { Download, Building2, Calendar, Users, TrendingUp, ChevronRight } from 'lucide-react';
import { format, parseISO, differenceInDays, startOfYear, endOfYear, eachMonthOfInterval } from 'date-fns';
import { dateLocales } from '../i18n';
import LoadingSpinner from '../components/LoadingSpinner';
import { useNotification } from '../contexts/NotificationContext';

const COLORS = ['#0088FE', '#00C49F', '#FFBB28', '#FF8042', '#8884d8'];

interface PropertyStats {
  id: string;
  name: string;
  bookings: number;
  occupancyRate: number;
  nights: number;
}

interface MonthlyStats {
  month: string;
  bookings: number;
  occupancyRate: number;
  averageNightsPerBooking: number;
}

interface GlobalStats {
  totalProperties: number;
  totalBookings: number;
  averageNightsPerBooking: number;
  averageOccupancyRate: number;
}

function StatCard({ title, value, icon: Icon, color }: {
  title: string;
  value: string | number;
  icon: React.ElementType;
  color: string;
}) {
  return (
    <div className="bg-white rounded-lg shadow p-4 sm:p-6">
      <div className="flex items-center justify-between">
        <div>
          <p className="text-sm font-medium text-gray-600">{title}</p>
          <p className="text-xl sm:text-2xl font-semibold text-gray-900 mt-1">{value}</p>
        </div>
        <div className={`p-3 rounded-full ${color}`}>
          <Icon className="w-5 h-5 sm:w-6 sm:h-6 text-white" />
        </div>
      </div>
    </div>
  );
}

export default function Statistics() {
  const { t, i18n } = useTranslation();
  const { addNotification } = useNotification();
  const [selectedYear, setSelectedYear] = useState(new Date().getFullYear());
  const [propertyStats, setPropertyStats] = useState<PropertyStats[]>([]);
  const [monthlyStats, setMonthlyStats] = useState<MonthlyStats[]>([]);
  const [globalStats, setGlobalStats] = useState<GlobalStats>({
    totalProperties: 0,
    totalBookings: 0,
    averageNightsPerBooking: 0,
    averageOccupancyRate: 0
  });
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  async function fetchStatistics() {
    try {
      setIsLoading(true);
      setError(null);

      // Récupérer les propriétés
      const propertiesResponse = await supabase
        .from('properties')
        .select('id, name');

      if (propertiesResponse.error) {
        throw new Error(`Erreur lors de la récupération des propriétés: ${propertiesResponse.error.message}`);
      }

      const properties = propertiesResponse.data || [];

      if (!properties.length) {
        setPropertyStats([]);
        setMonthlyStats([]);
        setGlobalStats({
          totalProperties: 0,
          totalBookings: 0,
          averageNightsPerBooking: 0,
          averageOccupancyRate: 0
        });
        return;
      }

      const startDate = format(startOfYear(new Date(selectedYear, 0)), 'yyyy-MM-dd');
      const endDate = format(endOfYear(new Date(selectedYear, 0)), 'yyyy-MM-dd');

      // Récupérer les réservations
      const bookingsResponse = await supabase
        .from('bookings')
        .select('id, property_id, check_in_date, check_out_date')
        .or(`and(check_in_date.lte.${endDate},check_out_date.gte.${startDate})`);

      if (bookingsResponse.error) {
        throw new Error(`Erreur lors de la récupération des réservations: ${bookingsResponse.error.message}`);
      }

      const bookings = bookingsResponse.data || [];

      // Calculer les statistiques par propriété
      const propertyStatsData = properties.map(property => {
        const propertyBookings = bookings.filter(b => b.property_id === property.id);
        const occupiedDays = new Set<string>();
        let totalNights = 0;
        
        propertyBookings.forEach(booking => {
          const checkIn = parseISO(booking.check_in_date);
          const checkOut = parseISO(booking.check_out_date);
          const nights = differenceInDays(checkOut, checkIn);
          totalNights += nights;
          
          let currentDate = checkIn;
          while (currentDate < checkOut) {
            const dateStr = format(currentDate, 'yyyy-MM-dd');
            if (dateStr >= startDate && dateStr <= endDate) {
              occupiedDays.add(dateStr);
            }
            currentDate = new Date(currentDate.getTime() + 24 * 60 * 60 * 1000);
          }
        });

        return {
          id: property.id,
          name: property.name,
          bookings: propertyBookings.length,
          nights: occupiedDays.size,
          occupancyRate: (occupiedDays.size / 365) * 100
        };
      });

      // Calculer les statistiques mensuelles
      const months = eachMonthOfInterval({
        start: new Date(selectedYear, 0, 1),
        end: new Date(selectedYear, 11, 31)
      });

      const monthlyStatsData = months.map(month => {
        const monthStart = new Date(selectedYear, month.getMonth(), 1);
        const monthEnd = new Date(selectedYear, month.getMonth() + 1, 0);

        const monthBookings = bookings.filter(b => {
          const checkIn = parseISO(b.check_in_date);
          const checkOut = parseISO(b.check_out_date);
          return checkOut > monthStart && checkIn < monthEnd;
        });

        let totalNights = 0;
        const occupiedDaysByProperty: { [key: string]: Set<string> } = {};

        monthBookings.forEach(booking => {
          const checkIn = parseISO(booking.check_in_date);
          const checkOut = parseISO(booking.check_out_date);
          const nights = differenceInDays(checkOut, checkIn);
          totalNights += nights;

          if (!occupiedDaysByProperty[booking.property_id]) {
            occupiedDaysByProperty[booking.property_id] = new Set();
          }

          let currentDate = checkIn < monthStart ? monthStart : checkIn;
          const endDate = checkOut > monthEnd ? monthEnd : checkOut;

          while (currentDate < endDate) {
            occupiedDaysByProperty[booking.property_id].add(format(currentDate, 'yyyy-MM-dd'));
            currentDate = new Date(currentDate.getTime() + 24 * 60 * 60 * 1000);
          }
        });

        const totalOccupiedDays = Object.values(occupiedDaysByProperty).reduce(
          (sum, days) => sum + days.size,
          0
        );
        const daysInMonth = differenceInDays(monthEnd, monthStart) + 1;
        const occupancyRate = (totalOccupiedDays / (properties.length * daysInMonth)) * 100;

        return {
          month: format(month, 'MMMM', { locale: dateLocales[i18n.language] }),
          bookings: monthBookings.length,
          occupancyRate,
          averageNightsPerBooking: monthBookings.length > 0 ? totalNights / monthBookings.length : 0
        };
      });

      // Calculer les statistiques globales
      const totalBookings = bookings.length;
      const totalNights = bookings.reduce((sum, booking) => {
        return sum + differenceInDays(parseISO(booking.check_out_date), parseISO(booking.check_in_date));
      }, 0);

      const totalOccupiedDays = propertyStatsData.reduce((sum, stat) => sum + stat.nights, 0);
      const averageOccupancyRate = (totalOccupiedDays / (properties.length * 365)) * 100;

      setPropertyStats(propertyStatsData);
      setMonthlyStats(monthlyStatsData);
      setGlobalStats({
        totalProperties: properties.length,
        totalBookings,
        averageNightsPerBooking: totalBookings > 0 ? totalNights / totalBookings : 0,
        averageOccupancyRate
      });

    } catch (error: any) {
      console.error('Error fetching statistics:', error);
      const errorMessage = error.message || 'Une erreur est survenue lors du chargement des statistiques';
      setError(errorMessage);
      addNotification('error', errorMessage);
      
      // Réinitialiser les états en cas d'erreur
      setPropertyStats([]);
      setMonthlyStats([]);
      setGlobalStats({
        totalProperties: 0,
        totalBookings: 0,
        averageNightsPerBooking: 0,
        averageOccupancyRate: 0
      });
    } finally {
      setIsLoading(false);
    }
  }

  useEffect(() => {
    fetchStatistics();
  }, [selectedYear]);

  function generateCSV() {
    const headers = [t('common.property'), t('statistics.metrics.bookings'), t('statistics.metrics.nights'), t('statistics.metrics.occupancyRate')];
    const propertyRows = propertyStats.map(stat => [
      stat.name,
      stat.bookings,
      stat.nights,
      `${stat.occupancyRate.toFixed(1)}%`
    ]);

    const monthlyHeaders = [t('common.month'), t('statistics.metrics.bookings'), t('statistics.metrics.averageNights'), t('statistics.metrics.occupancyRate')];
    const monthlyRows = monthlyStats.map(stat => [
      stat.month,
      stat.bookings,
      stat.averageNightsPerBooking.toFixed(1),
      `${stat.occupancyRate.toFixed(1)}%`
    ]);

    return [
      t('statistics.charts.propertyDetails'),
      headers.join(';'),
      ...propertyRows.map(row => row.join(';')),
      '',
      t('statistics.charts.monthlyEvolution'),
      monthlyHeaders.join(';'),
      ...monthlyRows.map(row => row.join(';'))
    ].join('\n');
  }

  function downloadCSV(csv: string, filename: string) {
    const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
    const link = document.createElement('a');
    link.href = URL.createObjectURL(blob);
    link.download = filename;
    link.click();
  }

  if (isLoading) {
    return <LoadingSpinner />;
  }

  if (error) {
    return (
      <div className="p-4 sm:p-6 text-center">
        <div className="bg-red-50 text-red-800 p-4 rounded-lg inline-block">
          <p className="font-medium">{t('common.error')}</p>
          <p className="text-sm mt-1">{error}</p>
          <button
            onClick={fetchStatistics}
            className="mt-4 px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 flex items-center gap-2 mx-auto"
          >
            <TrendingUp size={20} />
            {t('common.refresh')}
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="p-4 sm:p-6 space-y-6">
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
        <h1 className="text-2xl font-bold text-gray-800">{t('statistics.title')}</h1>
        <div className="flex flex-col sm:flex-row items-stretch sm:items-center gap-4 w-full sm:w-auto">
          <select
            value={selectedYear}
            onChange={(e) => setSelectedYear(Number(e.target.value))}
            className="rounded-lg border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500"
          >
            {[...Array(5)].map((_, i) => {
              const year = new Date().getFullYear() - i;
              return (
                <option key={year} value={year}>
                  {year}
                </option>
              );
            })}
          </select>
          <button
            onClick={() => {
              const csv = generateCSV();
              downloadCSV(csv, `${t('statistics.title')}_${selectedYear}.csv`);
            }}
            className="flex items-center justify-center gap-2 px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700"
          >
            <Download size={20} />
            <span>{t('common.export')}</span>
          </button>
        </div>
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
        <StatCard
          title={t('common.properties')}
          value={globalStats.totalProperties}
          icon={Building2}
          color="bg-blue-500"
        />
        <StatCard
          title={t('common.bookings')}
          value={globalStats.totalBookings}
          icon={Calendar}
          color="bg-green-500"
        />
        <StatCard
          title={t('statistics.metrics.averageNights')}
          value={Math.round(globalStats.averageNightsPerBooking)}
          icon={Users}
          color="bg-purple-500"
        />
        <StatCard
          title={t('statistics.metrics.occupancyRate')}
          value={`${globalStats.averageOccupancyRate.toFixed(1)}%`}
          icon={TrendingUp}
          color="bg-orange-500"
        />
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <div className="bg-white rounded-lg shadow p-4 sm:p-6">
          <h2 className="text-lg font-semibold mb-4">{t('statistics.charts.monthlyEvolution')}</h2>
          <div className="h-60 sm:h-80">
            <ResponsiveContainer width="100%" height="100%">
              <LineChart data={monthlyStats}>
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis 
                  dataKey="month" 
                  tick={{ fontSize: 12 }}
                  interval="preserveStartEnd"
                  angle={-45}
                  textAnchor="end"
                  height={60}
                />
                <YAxis yAxisId="left" orientation="left" />
                <YAxis yAxisId="right" orientation="right" />
                <Tooltip />
                <Legend />
                <Line
                  yAxisId="right"
                  type="monotone"
                  dataKey="occupancyRate"
                  name={t('statistics.metrics.occupancyRate')}
                  stroke="#0088FE"
                />
                <Line
                  yAxisId="left"
                  type="monotone"
                  dataKey="averageNightsPerBooking"
                  name={t('statistics.metrics.averageNights')}
                  stroke="#00C49F"
                />
              </LineChart>
            </ResponsiveContainer>
          </div>
        </div>

        <div className="bg-white rounded-lg shadow p-4 sm:p-6">
          <h2 className="text-lg font-semibold mb-4">{t('statistics.charts.occupancyByProperty')}</h2>
          <div className="h-60 sm:h-80">
            <ResponsiveContainer width="100%" height="100%">
              <PieChart>
                <Pie
                  data={propertyStats}
                  dataKey="occupancyRate"
                  nameKey="name"
                  cx="50%"
                  cy="50%"
                  outerRadius={80}
                  label={({ name, value }) => `${name} (${value.toFixed(1)}%)`}
                >
                  {propertyStats.map((_, index) => (
                    <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                  ))}
                </Pie>
                <Tooltip />
                <Legend />
              </PieChart>
            </ResponsiveContainer>
          </div>
        </div>
      </div>

      <div className="bg-white rounded-lg shadow overflow-hidden">
        <div className="p-4 sm:p-6">
          <h2 className="text-lg font-semibold mb-4">{t('statistics.charts.propertyDetails')}</h2>
          
          {/* Vue mobile - Cards */}
          <div className="block lg:hidden space-y-4">
            {propertyStats.map((stat, index) => (
              <div key={stat.id} className="bg-white rounded-lg shadow p-4">
                <div className="flex items-center justify-between mb-2">
                  <h3 className="font-medium text-gray-900">{stat.name}</h3>
                  <div className="w-3 h-3 rounded-full" style={{ backgroundColor: COLORS[index % COLORS.length] }} />
                </div>
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <p className="text-sm text-gray-500">{t('statistics.metrics.bookings')}</p>
                    <p className="text-lg font-semibold">{stat.bookings}</p>
                  </div>
                  <div>
                    <p className="text-sm text-gray-500">{t('statistics.metrics.nights')}</p>
                    <p className="text-lg font-semibold">{stat.nights}</p>
                  </div>
                  <div className="col-span-2">
                    <p className="text-sm text-gray-500">{t('statistics.metrics.occupancyRate')}</p>
                    <div className="mt-1 flex items-center gap-2">
                      <div className="flex-1 bg-gray-200 rounded-full h-2">
                        <div
                          className="bg-blue-600 h-2 rounded-full"
                          style={{ width: `${Math.min(100, Math.round(stat.occupancyRate))}%` }}
                        />
                      </div>
                      <span className="text-sm font-medium">{stat.occupancyRate.toFixed(1)}%</span>
                    </div>
                  </div>
                </div>
              </div>
            ))}
          </div>

          {/* Vue desktop - Tableau */}
          <div className="hidden lg:block">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    {t('common.property')}
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    {t('statistics.metrics.bookings')}
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    {t('statistics.metrics.nights')}
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    {t('statistics.metrics.occupancyRate')}
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {propertyStats.map((stat) => (
                  <tr key={stat.id} className="hover:bg-gray-50">
                    <td className="px-6 py-4 whitespace-nowrap">
                      {stat.name}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      {stat.bookings}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      {stat.nights}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="flex items-center">
                        <div className="w-full bg-gray-200 rounded-full h-2.5 mr-2 max-w-[200px]">
                          <div
                            className="bg-blue-600 h-2.5 rounded-full"
                            style={{ width: `${Math.min(100, Math.round(stat.occupancyRate))}%` }}
                          />
                        </div>
                        <span>{stat.occupancyRate.toFixed(1)}%</span>
                      </div>
                    </td>
                  </tr>
                ))}
                {propertyStats.length === 0 && (
                  <tr>
                    <td colSpan={4} className="px-6 py-4 text-center text-gray-500">
                      {t('common.noData')}
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        </div>
      </div>
    </div>
  );
}